import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import BrandHeader from '../../components/brand/BrandHeader';

import xdate from '../../../../../util/date.js';

type Asset = {
  id: string;
  name: string;
  asset_type: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  ratio?: string;
  ts?: {insert: Date, modified: Date};
  campaigns_count?: number;
};

function AssetCard({ asset, onDelete }: { asset: Asset; onDelete: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = asset.mime_type?.startsWith('video/');

  const handleMouseEnter = () => {
    if (videoRef.current && isVideo) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current && isVideo) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all">
      {}
      <div 
        className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center relative group overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isVideo ? (
          <>
            <video
              ref={videoRef}
              src={asset.file_url}
              className="w-full h-full object-contain"
              muted
              loop
            />
            {}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
              <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                </svg>
              </div>
            </div>
          </>
        ) : asset.mime_type?.startsWith('image/') ? (
          <img 
            src={asset.file_url} 
            alt={asset.name} 
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="text-white text-4xl">📄</div>
        )}

        {}
        {asset.campaigns_count !== undefined && asset.campaigns_count > 0 && (
          <div className="absolute top-3 left-3">
            <div className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
              </svg>
              {asset.campaigns_count}
            </div>
          </div>
        )}

        {}
        {asset.ratio && (
          <div className="absolute bottom-3 left-3 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {asset.ratio}
          </div>
        )}

        {}
        <a
          href={asset.file_url}
          target="_blank" 
          rel="noopener noreferrer" 
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-3 right-3 px-3 py-1.5 bg-white text-gray-900 rounded-lg text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100"
        >
          View Full
        </a>
      </div>

      {}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
          {asset.name}
        </h3>

        <div className="space-y-2 text-sm text-gray-600 mb-4">
          {asset.ratio && (
            <div className="flex items-center justify-between">
              <span>Ratio</span>
              <span className="font-medium text-gray-900">
                {asset.ratio}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span>Size</span>
            <span className="font-medium text-gray-900">
              {(asset.file_size / 1024 / 1024).toFixed(2)} MB
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Uploaded</span>
            <span className="font-medium text-gray-900">
              {asset.ts?.insert ? xdate.fmt_lbl_short(asset.ts.insert) : ''}
            </span>
          </div>
          {asset.campaigns_count !== undefined && (
            <div className="flex items-center justify-between">
              <span>Campaigns</span>
              <span className="font-medium text-purple-600">
                {asset.campaigns_count}
              </span>
            </div>
          )}
        </div>

        {}
        <div className="grid grid-cols-2 gap-2">
          <a 
            href={asset.file_url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all text-center"
          >
            View
          </a>
          <button 
            onClick={onDelete} 
            className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 transition-all"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AssetLibrary() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const result = await back_app.basset_list();

      if (!result.err && Array.isArray(result)) {
        const validAssets = result.filter(
          (a: Asset) => a.asset_type === 'video_ad' || a.asset_type === 'banner'
        );
        
        const assetsWithCounts = await Promise.all(
          validAssets.map(async (asset: Asset) => {
            try {
              const campaignsResult = await back_app.basset_camp_list(asset.id);
              return {
                ...asset,
                campaigns_count: !campaignsResult.err && Array.isArray(campaignsResult) ? campaignsResult.length : 0
              };
            } catch (error) {
              console.error(`Error fetching campaigns for asset ${asset.id}:`, error);
              return { ...asset, campaigns_count: 0 };
            }
          })
        );
        
        setAssets(assetsWithCounts);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assetId: string) => {
    if (!confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      const result = await back_app.basset_delete(assetId);

      if (!result.err) {
        alert('Asset deleted successfully');
        fetchAssets();
      } else {
        alert('Error deleting asset');
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      if ((error as Error)?.message == 'no session') alert('Not authenticated');
      else alert('Error deleting asset');
    }
  };

  
  const filteredAssets = useMemo(() => {
    let filtered = assets;

    
    if (filterType !== 'all') {
      filtered = filtered.filter(a => a.asset_type === filterType);
    }

    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [assets, filterType, searchQuery]);

  const assetTypeCounts = {
    all: assets.length,
    video_ad: assets.filter(a => a.asset_type === 'video_ad').length,
    banner: assets.filter(a => a.asset_type === 'banner').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Asset Library</h2>
            <p className="text-gray-600 mt-1">
              {assets.length} {assets.length === 1 ? 'asset' : 'assets'} uploaded
            </p>
          </div>
          <button
            onClick={() => navigate('/brand/upload-asset')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
          >
            + Upload Asset
          </button>
        </div>

        {}
        <div className="bg-white rounded-xl border p-4 mb-4">
          <input
            type="text"
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {}
        <div className="bg-white rounded-xl border p-2 mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                filterType === 'all' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              All ({assetTypeCounts.all})
            </button>
            <button
              onClick={() => setFilterType('video_ad')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                filterType === 'video_ad' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Video Ads ({assetTypeCounts.video_ad})
            </button>
            <button
              onClick={() => setFilterType('banner')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                filterType === 'banner' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Banners ({assetTypeCounts.banner})
            </button>
          </div>
        </div>

        {}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading assets...</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="text-6xl mb-4">
              {searchQuery || filterType !== 'all' ? '🔍' : '📁'}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {searchQuery || filterType !== 'all' ? 'No assets found' : 'No assets yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Upload your first asset to get started'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <button
                onClick={() => navigate('/brand/upload-asset')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Upload Asset
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredAssets.map((asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                onDelete={() => handleDelete(asset.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}